import { useMemo } from "react";
import { ChartLine } from "components/charting/types/Chart.types";
import { MetadataType } from "types/metadata";
import { Variants } from "types";
import ChartHelpers from "features/Charting/helpers/ChartHelpers";
import useXScale from "../useXScale";
import useYScaleById from "../useYScaleById";
import useChartPersonalBests from "components/charting/contexts/ChartPersonalBestsContext/hooks/useChartPersonalBests";

interface UseChartPbApiProps extends Pick<ChartLine, "metadataType" | "measure" | "source"> {}

interface PbLineData {
  variant: Variants | null;
  value: number;
  completedDate: string;
  pb: { variant: Variants | null; x: number; y: number }[];
}

function useChartPbApi(line: UseChartPbApiProps): PbLineData[] {
  const { metadataType, measure, source } = line;
  const athleteId = source.id;
  const exerciseId = measure.id;
  const scaleId = ChartHelpers.getScaleId(measure);
  const xScale = useXScale();
  const yScale = useYScaleById(scaleId);

  const personalBests = useChartPersonalBests();

  const personalBest = useMemo(() => {
    if (metadataType !== MetadataType.EXERCISE) return null;
    return (
      personalBests.find(
        (pb) => pb.athleteId === athleteId && pb.exerciseId === exerciseId && pb.metricField === measure.metricField
      ) || null
    );
  }, [athleteId, exerciseId, measure.metricField, metadataType, personalBests]);

  return useMemo((): PbLineData[] => {
    if (!personalBest || personalBest.value === null) return [];

    const y = yScale(personalBest.value);
    const xRange = xScale.range();

    // Return a single PB entry (no variant iteration needed - MeasureAggregate returns best across all variants)
    return [
      {
        variant: null,
        value: personalBest.value,
        completedDate: personalBest.date || "",
        pb: xRange.map((x) => ({ variant: null, x, y })),
      },
    ];
  }, [personalBest, xScale, yScale]);
}

export default useChartPbApi;
